// pages/wallet/payout-methods.jsx
import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, CreditCard, Shield, AlertCircle,
  CheckCircle, AlertTriangle, Info, Check, Copy
} from 'lucide-react';
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useAuth } from '../../context/AuthContext';
import {
  getPayoutMethods,
  deletePayoutMethod,
  setDefaultPayoutMethod,
  addPayoutMethod,
  verifyAccountAndName,
  getNigerianBanks
} from '../../services/api';
import { BankService } from '../../services/BankService';
import BankForm from '../../components/payout/BankForm';
import PayoutMethodsList from '../../components/payout/PayoutMethodsList';

const PayoutMethodsPage = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(true);
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  
  // Bank selection states
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  
  // Account verification states
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [accountVerificationResult, setAccountVerificationResult] = useState(null);
  const [nameVerificationStatus, setNameVerificationStatus] = useState(null);
  const [showAccountNameField, setShowAccountNameField] = useState(false);
  
  // New payout method form
  const [newMethod, setNewMethod] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    bank_code: '',
    is_default: false
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
      return;
    }
    fetchPayoutMethods();
    loadBanks();
  }, [authUser, navigate]);

  // API Calls
  const loadBanks = useCallback(async () => {
    try {
      setLoadingBanks(true);
      const bankList = await BankService.getBanksWithCache();
      setBanks(bankList);
    } catch (error) {
      // console.error('Failed to load banks:', error);
      setError('Failed to load bank list. Please refresh the page.');
    } finally {
      setLoadingBanks(false);
    }
  }, []);

  const fetchPayoutMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await getPayoutMethods(authUser.user_id);
      
      if (response && response.success) {
        const methods = (response.payout_methods || []).map(method => {
          let details = {};
          try {
            if (typeof method.details === 'string') {
              details = JSON.parse(method.details);
            } else if (method.details && typeof method.details === 'object') {
              details = method.details;
            }
          } catch (e) {
            // console.error('Error parsing method details:', e);
            details = {};
          }
          
          return {
            ...method,
            details: details,
            verification_status: method.verification_status || {}
          };
        });
        
        setPayoutMethods(methods);
      } else {
        setError('Failed to load payout methods');
      }
    } catch (error) {
      // console.error('Error fetching payout methods:', error);
      setError('Failed to load payout methods. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authUser?.user_id]);

  // Enhanced account verification with name matching
  const verifyBankAccountWithName = useCallback(async () => {
    const cleanAccountNumber = newMethod.account_number.replace(/\D/g, '');
    const cleanBankCode = String(newMethod.bank_code).trim();
    
    // Skip if already verified this exact account/bank combination
    if (accountVerificationResult && 
        accountVerificationResult.account_number === cleanAccountNumber &&
        accountVerificationResult.bank_details?.bank_id?.toString() === cleanBankCode) {
      // console.log('Already verified this account/bank combination');
      return;
    }
    
    if (cleanAccountNumber.length !== 10) {
      setError('Account number must be exactly 10 digits');
      setShowAccountNameField(true); // Show field for manual entry
      return;
    }
    
    if (!/^\d{10}$/.test(cleanAccountNumber)) {
      setError('Account number must contain only numbers');
      setShowAccountNameField(true);
      return;
    }
    
    if (!cleanBankCode) {
      setError('Please select a bank first');
      return;
    }
    
    try {
      setVerifyingAccount(true);
      setError('');
      setAccountVerificationResult(null);
      setNameVerificationStatus(null);
      setShowAccountNameField(false); // Hide field while verifying

      // Don't send account_name - let Paystack provide verified name
      const response = await verifyAccountAndName({
        account_number: cleanAccountNumber,
        bank_code: cleanBankCode
      });

      if (response.success) {
        setAccountVerificationResult(response.verification);
        setNameVerificationStatus(response.verification.name_verification);
        
        // Auto-fill account name with verified name from Paystack
        setNewMethod(prev => ({
          ...prev,
          account_name: response.verification.verified_account_name
        }));
        
        // Keep field hidden (auto-filled)
        setShowAccountNameField(false);

        if (response.verification.name_verification.success) {
          if (response.verification.name_verification.auto_updated) {
            setSuccess(`✅ Profile updated automatically: ${response.verification.verified_account_name}`);
          } else if (response.verification.name_verification.match) {
            setSuccess(`✅ Account verified. Name matches your profile: ${response.verification.verified_account_name}`);
          }
        } else {
          setError(`⚠️ ${response.verification.name_verification.message || 'Name verification failed'}`);
          // Verification failed - show field for manual correction
          setShowAccountNameField(true);
        }
      } else {
        // Paystack verification failed - show field for manual entry
        setError(response.error || 'Account verification failed. Please enter account name manually.');
        setShowAccountNameField(true);
      }
    } catch (error) {
      // console.error('Account verification error:', error);
      setError('Failed to verify account. Please enter account name manually.');
      setShowAccountNameField(true); // Show field for manual entry
    } finally {
      setVerifyingAccount(false);
    }
  }, [newMethod, accountVerificationResult]);

  // Auto-verify when account number changes (with improved logic)
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     const cleanAccountNumber = newMethod.account_number.replace(/\D/g, '');
      
  //     // Only auto-verify if we have 10 digits AND bank selected
  //     if (cleanAccountNumber.length === 10 && newMethod.bank_code) {
  //       // Skip if already verified this exact account/bank
  //       if (accountVerificationResult && 
  //           accountVerificationResult.account_number === cleanAccountNumber &&
  //           accountVerificationResult.bank_details?.bank_id?.toString() === newMethod.bank_code.toString()) {
  //         return;
  //       }
        
  //       verifyBankAccountWithName();
  //     } else if (cleanAccountNumber.length === 10 && !newMethod.bank_code) {
  //       // Have account number but no bank - show manual field
  //       setShowAccountNameField(true);
  //     } else if (cleanAccountNumber.length > 0 && cleanAccountNumber.length < 10) {
  //       // Partial account number - show manual field
  //       setShowAccountNameField(true);
  //     }
  //   }, 3000); // Increased to 3 seconds

  //   return () => clearTimeout(timer);
  // }, [newMethod.account_number, newMethod.bank_code, verifyBankAccountWithName, accountVerificationResult]);

  // Event Handlers
  const handleSetDefault = useCallback(async (payoutMethodId) => {
    try {
      setProcessing(true);
      setError('');
      setSuccess('');
      
      const response = await setDefaultPayoutMethod(authUser.user_id, payoutMethodId);
      
      if (response && response.success) {
        setPayoutMethods(prev => 
          prev.map(method => ({
            ...method,
            is_default: method.payout_method_id === payoutMethodId ? 1 : 0
          }))
        );
        setSuccess('Default payout method updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response?.error || 'Failed to set default method');
      }
    } catch (error) {
      // console.error('Error setting default method:', error);
      setError('Failed to set default method: ' + error.message);
    } finally {
      setProcessing(false);
    }
  }, [authUser?.user_id]);

  const handleDelete = useCallback(async (payoutMethodId) => {
    try {
      setDeletingId(payoutMethodId);
      setError('');
      setSuccess('');
      
      const response = await deletePayoutMethod(authUser.user_id, payoutMethodId);
      
      if (response && response.success) {
        setPayoutMethods(prev => 
          prev.filter(method => method.payout_method_id !== payoutMethodId)
        );
        setSuccess('Payout method deleted successfully');
        setShowDeleteConfirm(null);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response?.error || 'Failed to delete method');
      }
    } catch (error) {
      // console.error('Error deleting payout method:', error);
      setError('Failed to delete payout method: ' + error.message);
    } finally {
      setDeletingId(null);
    }
  }, [authUser?.user_id]);

  const handleShowDeleteConfirm = useCallback((methodId) => {
    setShowDeleteConfirm(prev => prev === methodId ? null : methodId);
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!newMethod.bank_name?.trim()) {
      errors.bank_name = 'Bank name is required';
    }
    
    if (!newMethod.account_number?.trim()) {
      errors.account_number = 'Account number is required';
    } else if (!/^\d{10}$/.test(newMethod.account_number.replace(/\s/g, ''))) {
      errors.account_number = 'Account number must be 10 digits';
    }
    
    if (!newMethod.account_name?.trim()) {
      errors.account_name = 'Account name is required';
    }
    
    if (!newMethod.bank_code?.trim()) {
      errors.bank_code = 'Bank code is required';
    }
    
    return errors;
  }, [newMethod]);

  const handleAddMethod = useCallback(async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!accountVerificationResult && !showAccountNameField) {
      setError('Please verify your bank account first');
      return;
    }

    if (nameVerificationStatus && !nameVerificationStatus.match && !nameVerificationStatus.auto_updated) {
      setError('Bank account name verification failed. Please contact support.');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      setSuccess('');
      
      const payoutData = {
        method_type: 'bank_account',
        details: {
          bank_name: newMethod.bank_name.trim(),
          account_number: newMethod.account_number.trim(),
          account_name: newMethod.account_name.trim(),
          bank_code: newMethod.bank_code.trim()
        },
        is_default: newMethod.is_default ? 1 : 0,
        verified_account_name: newMethod.account_name.trim()
      };
      
      const response = await addPayoutMethod(authUser.user_id, payoutData);
      
      if (response && response.success) {
        setSuccess('✅ Bank account added successfully!');
        
        // Reset form
        setNewMethod({
          bank_name: '',
          account_number: '',
          account_name: '',
          bank_code: '',
          is_default: false
        });
        setFormErrors({});
        setActiveTab('list');
        setAccountVerificationResult(null);
        setNameVerificationStatus(null);
        setShowAccountNameField(false);
        
        // Refresh payout methods list
        fetchPayoutMethods();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response?.error || 'Failed to add payout method');
      }
    } catch (error) {
      // console.error('Error adding payout method:', error);
      setError('Failed to add payout method: ' + error.message);
    } finally {
      setProcessing(false);
    }
  }, [newMethod, accountVerificationResult, nameVerificationStatus, showAccountNameField, authUser?.user_id, validateForm, fetchPayoutMethods]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'clearError') {
      setFormErrors(prev => ({
        ...prev,
        [value]: ''
      }));
      return;
    }
    
    setNewMethod(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // If user manually edits account name, show the field
    if (name === 'account_name' && value.trim()) {
      setShowAccountNameField(true);
    }
  }, [formErrors]);

  const handleBankSelect = useCallback((bank) => {
    setNewMethod(prev => ({
      ...prev,
      bank_name: bank.name,
      bank_code: bank.code
    }));
    
    if (formErrors.bank_name) {
      setFormErrors(prev => ({ ...prev, bank_name: '' }));
    }
    
    // Only clear verification if changing to a DIFFERENT bank
    if (!accountVerificationResult || accountVerificationResult.bank_code !== bank.code.toString()) {
      setAccountVerificationResult(null);
      setNameVerificationStatus(null);
      setShowAccountNameField(false);
    }
  }, [formErrors, accountVerificationResult]);

  const copyToClipboard = useCallback(async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      // console.error('Failed to copy:', err);
    }
  }, []);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setError('');
    setSuccess('');
  }, []);

  const handleCancel = useCallback(() => {
    setActiveTab('list');
    setNewMethod({
      bank_name: '',
      account_number: '',
      account_name: '',
      bank_code: '',
      is_default: false
    });
    setFormErrors({});
    setAccountVerificationResult(null);
    setNameVerificationStatus(null);
    setShowAccountNameField(false);
  }, []);

  if (loading && activeTab === 'list') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <>
      {/* Mobile View */}
      <MobilePayoutMethods
        activeTab={activeTab}
        onTabChange={handleTabChange}
        success={success}
        error={error}
        payoutMethods={payoutMethods}
        loading={loading}
        processing={processing}
        deletingId={deletingId}
        showDeleteConfirm={showDeleteConfirm}
        copiedField={copiedField}
        newMethod={newMethod}
        banks={banks}
        loadingBanks={loadingBanks}
        formErrors={formErrors}
        verifyingAccount={verifyingAccount}
        accountVerificationResult={accountVerificationResult}
        nameVerificationStatus={nameVerificationStatus}
        showAccountNameField={showAccountNameField}
        onInputChange={handleInputChange}
        onBankSelect={handleBankSelect}
        onAddMethod={handleAddMethod}
        onSetDefault={handleSetDefault}
        onDelete={handleDelete}
        onShowDeleteConfirm={handleShowDeleteConfirm}
        onCopyToClipboard={copyToClipboard}
        onCancel={handleCancel}
        navigate={navigate}
        authUser={authUser}
        verifyBankAccountWithName={verifyBankAccountWithName}
      />

      {/* Desktop View */}
      <DesktopPayoutMethods
        activeTab={activeTab}
        onTabChange={handleTabChange}
        success={success}
        error={error}
        payoutMethods={payoutMethods}
        processing={processing}
        deletingId={deletingId}
        showDeleteConfirm={showDeleteConfirm}
        copiedField={copiedField}
        newMethod={newMethod}
        banks={banks}
        loadingBanks={loadingBanks}
        formErrors={formErrors}
        verifyingAccount={verifyingAccount}
        accountVerificationResult={accountVerificationResult}
        nameVerificationStatus={nameVerificationStatus}
        showAccountNameField={showAccountNameField}
        onInputChange={handleInputChange}
        onBankSelect={handleBankSelect}
        onAddMethod={handleAddMethod}
        onSetDefault={handleSetDefault}
        onDelete={handleDelete}
        onShowDeleteConfirm={handleShowDeleteConfirm}
        onCopyToClipboard={copyToClipboard}
        onCancel={handleCancel}
        navigate={navigate}
        authUser={authUser}
        verifyBankAccountWithName={verifyBankAccountWithName}
      />
    </>
  );
};

// Static Mobile Components
const MobilePayoutMethods = memo(({
  activeTab,
  onTabChange,
  success,
  error,
  payoutMethods,
  processing,
  deletingId,
  showDeleteConfirm,
  copiedField,
  newMethod,
  banks,
  loadingBanks,
  formErrors,
  verifyingAccount,
  accountVerificationResult,
  nameVerificationStatus,
  showAccountNameField,
  onInputChange,
  onBankSelect,
  onAddMethod,
  onSetDefault,
  onDelete,
  onShowDeleteConfirm,
  onCopyToClipboard,
  onCancel,
  navigate,
  authUser,
  verifyBankAccountWithName
}) => (
  <div className="lg:hidden min-h-screen bg-gray-50">
    {/* Mobile Header */}
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/wallet')}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          
          <h1 className="text-lg font-bold text-black">
            {activeTab === 'add' ? 'Add Bank Account' : 'Payout Methods'}
          </h1>
          
          <div className="w-10"></div>
        </div>
      </div>
    </header>

    {/* Success/Error Messages */}
    <Messages success={success} error={error} />

    {/* Tab Navigation */}
    <TabNavigation activeTab={activeTab} onTabChange={onTabChange} />

    {/* Tab Content */}
    <div className="px-4 pb-20">
      {activeTab === 'list' ? (
        <div className="space-y-4">
          <button 
            onClick={() => onTabChange('add')}
            className="w-full bg-[#ff660e] text-white py-3.5 rounded-lg font-semibold text-sm flex items-center justify-center mb-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Bank Account
          </button>

          <UserVerificationStatus authUser={authUser} />

          <PayoutMethodsList
            payoutMethods={payoutMethods}
            processing={processing}
            deletingId={deletingId}
            showDeleteConfirm={showDeleteConfirm}
            onSetDefault={onSetDefault}
            onDelete={onDelete}
            onShowDeleteConfirm={onShowDeleteConfirm}
            copiedField={copiedField}
            onCopyToClipboard={onCopyToClipboard}
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-black">Add Bank Account</h3>
            <button
              onClick={onCancel}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <EnhancedBankForm
            newMethod={newMethod}
            banks={banks}
            loadingBanks={loadingBanks}
            formErrors={formErrors}
            verifyingAccount={verifyingAccount}
            accountVerificationResult={accountVerificationResult}
            nameVerificationStatus={nameVerificationStatus}
            showAccountNameField={showAccountNameField}
            onInputChange={onInputChange}
            onBankSelect={onBankSelect}
            onAddMethod={onAddMethod}
            processing={processing}
            onCancel={onCancel}
            verifyBankAccountWithName={verifyBankAccountWithName}
          />
        </div>
      )}
    </div>

    <MobileBottomNav />
  </div>
));

// Static Desktop Components
const DesktopPayoutMethods = memo(({
  activeTab,
  onTabChange,
  success,
  error,
  payoutMethods,
  processing,
  deletingId,
  showDeleteConfirm,
  copiedField,
  newMethod,
  banks,
  loadingBanks,
  formErrors,
  verifyingAccount,
  accountVerificationResult,
  nameVerificationStatus,
  showAccountNameField,
  onInputChange,
  onBankSelect,
  onAddMethod,
  onSetDefault,
  onDelete,
  onShowDeleteConfirm,
  onCopyToClipboard,
  onCancel,
  navigate,
  authUser,
  verifyBankAccountWithName
}) => (
  <div className="hidden lg:block min-h-screen bg-gray-50">
    <Header />
    
    <main className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/wallet')}
            className="flex items-center gap-2 text-gray-600 hover:text-black mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Wallet</span>
          </button>
          <h1 className="text-3xl font-bold text-black">Payout Methods</h1>
          <p className="text-gray-600 mt-1">Manage your bank accounts for withdrawals</p>
        </div>
        
        <button
          onClick={() => onTabChange('add')}
          className="flex items-center gap-2 px-6 py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#e55a0d]"
        >
          <Plus className="w-5 h-5" />
          Add Bank Account
        </button>
      </div>

      {/* Success/Error Messages */}
      <Messages success={success} error={error} />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Column - Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
            <nav className="space-y-1">
              <button
                onClick={() => onTabChange('list')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === 'list'
                    ? 'bg-[#ff660e] text-white'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <CreditCard className="w-5 h-5" />
                <span className="font-medium">My Accounts</span>
                <span className="ml-auto text-sm bg-gray-100 px-2 py-1 rounded">
                  {payoutMethods.length}
                </span>
              </button>

              <button
                onClick={() => onTabChange('add')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === 'add'
                    ? 'bg-[#ff660e] text-white'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add New</span>
              </button>
            </nav>

            <UserVerificationStatus authUser={authUser} desktop />
          </div>
        </div>

        {/* Right Column - Main Content */}
        <div className="lg:col-span-3">
          {activeTab === 'list' ? (
            <DesktopPayoutMethodsList
              payoutMethods={payoutMethods}
              processing={processing}
              deletingId={deletingId}
              showDeleteConfirm={showDeleteConfirm}
              onSetDefault={onSetDefault}
              onDelete={onDelete}
              onShowDeleteConfirm={onShowDeleteConfirm}
              copiedField={copiedField}
              onCopyToClipboard={onCopyToClipboard}
            />
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-black">Add New Bank Account</h2>
                  <p className="text-gray-600 mt-1">Add a verified bank account for withdrawals</p>
                </div>
                <button
                  onClick={onCancel}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <EnhancedBankForm
                newMethod={newMethod}
                banks={banks}
                loadingBanks={loadingBanks}
                formErrors={formErrors}
                verifyingAccount={verifyingAccount}
                accountVerificationResult={accountVerificationResult}
                nameVerificationStatus={nameVerificationStatus}
                showAccountNameField={showAccountNameField}
                onInputChange={onInputChange}
                onBankSelect={onBankSelect}
                onAddMethod={onAddMethod}
                processing={processing}
                onCancel={onCancel}
                verifyBankAccountWithName={verifyBankAccountWithName}
              />
            </div>
          )}
        </div>
      </div>
    </main>
    
    <Footer />
  </div>
));

// Shared Static Components
const Messages = memo(({ success, error }) => (
  <>
    {(success || error) && (
      <div className="px-4 py-3">
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}
      </div>
    )}
  </>
));

const TabNavigation = memo(({ activeTab, onTabChange }) => (
  <div className="px-4 mb-4">
    <div className="flex overflow-x-auto space-x-2 pb-2">
      {[
        { id: 'list', label: 'My Accounts' },
        { id: 'add', label: 'Add New' }
      ].map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
            activeTab === tab.id
              ? 'bg-[#ff660e] text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>
));

const UserVerificationStatus = memo(({ authUser, desktop = false }) => {
  const getUserVerifiedName = () => {
    if (authUser?.verified_name) {
      return authUser.verified_name;
    }
    if (authUser?.first_name && authUser?.last_name) {
      return `${authUser.first_name} ${authUser.middle_name || ''} ${authUser.last_name}`.trim();
    }
    return '';
  };

  if (desktop) {
    return (
      <div className="mt-6 pt-6 border-t">
        <div className={`p-4 rounded-lg ${
          authUser?.name_verified ? 'bg-green-50' : 'bg-yellow-50'
        }`}>
          <div className="flex items-start gap-3">
            {authUser?.name_verified ? (
              <Shield className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            )}
            <div>
              <p className="font-medium text-sm">
                {authUser?.name_verified ? 'Name Verified' : 'Verification Required'}
              </p>
              <p className="text-xs mt-1">
                {authUser?.name_verified 
                  ? `Your name is verified: ${getUserVerifiedName()}`
                  : 'Add your first bank account to verify your name'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {authUser?.name_verified ? (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
          <div className="flex items-center">
            <Shield className="w-4 h-4 text-green-600 mr-2" />
            <div>
              <p className="text-xs font-medium text-green-800">Name Verified</p>
              <p className="text-xs text-green-700">Your name is verified: {getUserVerifiedName()}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
          <div className="flex items-center">
            <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
            <div>
              <p className="text-xs font-medium text-yellow-800">Name Not Verified</p>
              <p className="text-xs text-yellow-700">Add your first bank account to verify your name</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

// Enhanced Bank Form Component
const EnhancedBankForm = memo(({
  newMethod,
  banks,
  loadingBanks,
  formErrors,
  verifyingAccount,
  accountVerificationResult,
  nameVerificationStatus,
  showAccountNameField,
  onInputChange,
  onBankSelect,
  onAddMethod,
  processing,
  onCancel,
  verifyBankAccountWithName
}) => {
  const canVerify = newMethod.account_number.replace(/\D/g, '').length === 10 && newMethod.bank_code;

  return (
    <div className="space-y-6">
      {/* Bank Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bank
        </label>
        <div className="relative">
          <select
            name="bank_code"
            value={newMethod.bank_code}
            onChange={(e) => {
              const selectedBank = banks.find(bank => bank.code === e.target.value);
              if (selectedBank) {
                onBankSelect(selectedBank);
              }
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
            disabled={loadingBanks}
            required
          >
            <option value="">Select Bank</option>
            {banks.map((bank) => (
              <option key={bank.code} value={bank.code}>
                {bank.name}
              </option>
            ))}
          </select>
          {loadingBanks && (
            <div className="absolute right-3 top-3">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
        {formErrors.bank_code && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bank_code}</p>
        )}
      </div>

      {/* Account Number */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Account Number
        </label>
        <input
          type="text"
          name="account_number"
          value={newMethod.account_number}
          onChange={onInputChange}
          maxLength="10"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="10-digit account number"
          required
        />
        {formErrors.account_number && (
          <p className="mt-1 text-sm text-red-600">{formErrors.account_number}</p>
        )}
      </div>

      {/* Account Name Field - Conditionally shown */}
      {showAccountNameField ? (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Name
          </label>
          <input
            type="text"
            name="account_name"
            value={newMethod.account_name}
            onChange={onInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter account name as it appears in your bank"
            required
          />
          {formErrors.account_name && (
            <p className="mt-1 text-sm text-red-600">{formErrors.account_name}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Enter the exact name as registered with your bank
          </p>
        </div>
      ) : (
        // Show verified name as read-only
        newMethod.account_name && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <label className="block text-sm font-medium text-green-700 mb-1">
              ✅ Verified Account Name
            </label>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-green-800">
                {newMethod.account_name}
              </span>
              <button
                type="button"
                onClick={() => onInputChange({ target: { name: 'clearError', value: 'account_name' } })}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
            </div>
            <p className="text-xs text-green-600 mt-1">
              Verified by Paystack. Click Edit to change manually.
            </p>
          </div>
        )
      )}

      {/* Manual Verification Button */}
      {canVerify && !accountVerificationResult && (
        <div className="mb-4">
          <button
            type="button"
            onClick={verifyBankAccountWithName}
            disabled={verifyingAccount}
            className="w-full py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#e55a0d] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifyingAccount ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Verifying...
              </div>
            ) : (
              'Verify Account'
            )}
          </button>
          <p className="text-xs text-gray-500 mt-1 text-center">
            We'll verify your account with your bank and auto-fill the account name
          </p>
        </div>
      )}

      {/* Verification Status Display */}
      {accountVerificationResult && (
        <div className="mb-4">
          <div className={`p-3 rounded-lg ${
            nameVerificationStatus?.match ? 'bg-green-50 border border-green-200' : 
            nameVerificationStatus?.auto_updated ? 'bg-blue-50 border border-blue-200' : 
            'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-start">
              {nameVerificationStatus?.match ? (
                <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              ) : nameVerificationStatus?.auto_updated ? (
                <Info className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="font-medium text-sm">
                  {nameVerificationStatus?.match ? 'Account Verified' : 
                   nameVerificationStatus?.auto_updated ? 'Profile Updated' : 
                   'Verification Required'}
                </p>
                <p className="text-xs mt-1">
                  {nameVerificationStatus?.message || 'Account verification completed'}
                </p>
                {nameVerificationStatus?.previous_name && (
                  <p className="text-xs mt-1">
                    <span className="font-medium">Previous:</span> {nameVerificationStatus.previous_name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set as Default */}
      <div className="mb-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            name="is_default"
            checked={newMethod.is_default}
            onChange={onInputChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Set as default payout method</span>
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onAddMethod}
          disabled={processing || !newMethod.account_name || !newMethod.account_number || !newMethod.bank_code}
          className="flex-1 py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#e55a0d] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Adding...
            </div>
          ) : (
            'Add Bank Account'
          )}
        </button>
      </div>
    </div>
  );
});

const DesktopPayoutMethodsList = memo(({
  payoutMethods,
  processing,
  deletingId,
  showDeleteConfirm,
  onSetDefault,
  onDelete,
  onShowDeleteConfirm,
  copiedField,
  onCopyToClipboard
}) => {
  const formatAccountNumber = (accountNumber) => {
    if (!accountNumber) return '';
    const cleaned = accountNumber.replace(/\D/g, '');
    if (cleaned.length !== 10) return accountNumber;
    return `${cleaned.substring(0, 4)}-${cleaned.substring(4, 8)}-${cleaned.substring(8, 10)}`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-black">Your Bank Accounts</h3>
            <p className="text-sm text-gray-600 mt-1">
              {payoutMethods.length} account{payoutMethods.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Secure & Verified</span>
          </div>
        </div>
      </div>

      {payoutMethods.length > 0 ? (
        <div className="divide-y divide-gray-200">
          {payoutMethods.map((method) => (
            <DesktopPayoutMethodCard
              key={method.payout_method_id}
              method={method}
              formatAccountNumber={formatAccountNumber}
              processing={processing}
              deletingId={deletingId}
              showDeleteConfirm={showDeleteConfirm}
              onSetDefault={onSetDefault}
              onDelete={onDelete}
              onShowDeleteConfirm={onShowDeleteConfirm}
              copiedField={copiedField}
              onCopyToClipboard={onCopyToClipboard}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-medium mb-2">No bank accounts yet</h4>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Add a bank account to receive withdrawals from your wallet.
          </p>
        </div>
      )}
    </div>
  );
});

const DesktopPayoutMethodCard = memo(({
  method,
  formatAccountNumber,
  processing,
  deletingId,
  showDeleteConfirm,
  onSetDefault,
  onDelete,
  onShowDeleteConfirm,
  copiedField,
  onCopyToClipboard
}) => {
  const handleCopy = useCallback(() => {
    onCopyToClipboard(method.details.account_number, `account-${method.payout_method_id}`);
  }, [method, onCopyToClipboard]);

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-black">{method.details.bank_name || 'Bank Account'}</h4>
                {method.is_default === 1 && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    Default
                  </span>
                )}
                {method.is_verified === 1 && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    Verified
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Account Number</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm text-gray-800">
                      {formatAccountNumber(method.details.account_number)}
                    </p>
                    <button
                      onClick={handleCopy}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      {copiedField === `account-${method.payout_method_id}` ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Account Name</p>
                  <p className="text-sm text-gray-800">{method.details.account_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Bank Code</p>
                  <p className="font-mono text-sm text-gray-800">{method.details.bank_code}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          {method.verification_status && (
            <div className={`p-3 rounded-lg text-sm ${
              method.verification_status.matches_profile 
                ? 'bg-green-50 text-green-700' 
                : 'bg-yellow-50 text-yellow-700'
            }`}>
              <div className="flex items-center">
                {method.verification_status.matches_profile ? (
                  <CheckCircle className="w-4 h-4 mr-2" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mr-2" />
                )}
                <div>
                  <p className="font-medium">
                    {method.verification_status.matches_profile 
                      ? 'Account name matches your profile' 
                      : 'Account name doesn\'t match profile'}
                  </p>
                  {!method.verification_status.matches_profile && (
                    <p className="mt-1 text-xs">
                      Bank: {method.verification_status.account_name} | 
                      Profile: {method.verification_status.user_name}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4">
          {method.is_default !== 1 && (
            <button
              onClick={() => onSetDefault(method.payout_method_id)}
              disabled={processing}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Set as Default
            </button>
          )}
          
          <button
            onClick={() => onShowDeleteConfirm(method.payout_method_id)}
            disabled={deletingId === method.payout_method_id}
            className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {deletingId === method.payout_method_id ? 'Deleting...' : 'Remove'}
          </button>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm === method.payout_method_id && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-800 mb-2">Delete Bank Account?</p>
              <p className="text-sm text-red-700 mb-3">
                Are you sure you want to delete this bank account? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => onDelete(method.payout_method_id)}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => onShowDeleteConfirm(null)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PayoutMethodsPage;